import { GifsResult } from '@giphy/js-fetch-api'
import { IGif } from '@giphy/js-types'
import { Gif, GifOverlayProps, Grid as SDKGrid } from '@giphy/react-components'
import { uniq, without } from 'lodash'
import React, {
    MouseEvent,
    ReactType,
    SyntheticEvent,
    useCallback,
    useContext,
    useEffect,
    useState,
    useRef,
} from 'react'
import { useHistory } from 'react-router'
import { useKeyPress } from 'react-use'
import BulkEditor from 'shared/components/bulk-editor'
import { useCanBulkEdit } from 'shared/hooks/use-permissions'
import { useSameObjectRef } from 'shared/hooks/use-same-ref'
import { grid12 } from 'shared/util/grid'
import { relativeUrl } from 'shared/util/url'
import styled, { css } from 'styled-components'
import BulkEditModeContext from './context/bulk-edit-mode'
import GAEventContext from './context/ga-events'
import GifContext from './context/gif'
import ModalContext from './context/modal'
import GridOverlay, { GridOverlayContext } from './grid-overlay'
import TV from './tv'
import { getGifWidthForTV, getTvProps, TVConfig, TVProps } from './tv-util'
import UserContext from './context/user'
import isMobile from 'shared/util/is-mobile'

const Container = styled.div`
    position: relative;
`
const GridWithTV = styled(SDKGrid)<{ offsetIndexes: number[]; offset: number }>`
    ${props =>
        props.offsetIndexes.map(
            (index: number) => css`
                .${Gif.className}:nth-child(${index + 1}) {
                    padding-top: ${props.offset}px;
                    box-sizing: content-box;
                }
            `
        )}
`

type Props = {
    className?: string
    fetchGifs: (offset: number) => Promise<GifsResult>
    width?: number
    standaloneSelectionMode?: boolean // for the gif add grid, need to think of a better name
    onSelectionUpdate?: (gifs: GifId[]) => void
    tvConfig?: TVConfig
    columns?: number
    gutter?: number
    Overlay?: ReactType<GifOverlayProps>
}
type GifId = string | number

const defaultGutter = isMobile() ? 6 : 16

const Grid = ({
    fetchGifs,
    width = grid12,
    gutter = defaultGutter,
    columns = 3,
    standaloneSelectionMode,
    onSelectionUpdate,
    className,
    tvConfig,
    Overlay,
}: Props) => {
    const { receivedGifs } = useContext(GifContext)
    const { openBulkEditPanel } = useContext(ModalContext)
    const { isBulkEditMode, setBulkEditGifIds: setSelectedGifs, bulkEditGifIds: selectedGifs } = useContext(
        BulkEditModeContext
    )
    const { user } = useContext(UserContext)
    const iSelectionMode = !!(isBulkEditMode || standaloneSelectionMode)
    const [gifs, setGifs] = useState<GifId[]>([])
    const history = useHistory()
    const { onGifClick: sendGAGifClick, onGifRightClick, onPagination } = useContext(GAEventContext)
    const onError = useCallback(e => console.error('Grid fetch error:', e), [])
    const [isEscPressed] = useKeyPress((event: KeyboardEvent) => event.keyCode === 27)
    const page = useRef(1)

    const allowBulkEdit = useCanBulkEdit()

    const fetchWrapped = async (offset: number) => {
        const result = await fetchGifs(offset)
        const { data: gifs = [] } = result
        setGifs(gifs.map(gif => gif.id))
        receivedGifs({ gifs })
        onPagination(page.current++)
        return result
    }

    const [lastSelected, setLastSelected] = useState<GifId | undefined>()

    useEffect(() => {
        setSelectedGifs([])
    }, [isEscPressed, setSelectedGifs])

    useEffect(() => {
        return () => setSelectedGifs([])
    }, [setSelectedGifs])

    useEffect(() => {
        onSelectionUpdate?.(selectedGifs)
    }, [selectedGifs, onSelectionUpdate])

    const onGifClick = useCallback(
        (gif: IGif, e: SyntheticEvent<HTMLElement, Event>) => {
            sendGAGifClick(gif, e)
            e.preventDefault()
            const { url } = gif
            const mouseEvent = (e as unknown) as MouseEvent
            if (!iSelectionMode) {
                if (mouseEvent.metaKey) {
                    window.open(url, '_blank')
                } else {
                    // default gif click history pushes
                    history.push(relativeUrl(gif.url))
                }
            } else {
                // Selection mode stuff
                if (!standaloneSelectionMode) {
                    // standaloneSelectionMode means no bulk editor
                    openBulkEditPanel(allowBulkEdit)
                }
                const { id } = gif
                if (mouseEvent.metaKey) {
                    window.open(url, '_blank')
                } else if (mouseEvent.shiftKey) {
                    const startIndex = lastSelected ? gifs.indexOf(lastSelected) : 0
                    const endIndex = gifs.indexOf(gif.id)
                    setSelectedGifs(
                        // TODO I think this is right...
                        uniq([
                            ...selectedGifs,
                            ...gifs.slice(Math.min(startIndex, endIndex), Math.max(startIndex, endIndex) + 1),
                        ])
                    )
                } else {
                    if (selectedGifs.indexOf(id) !== -1) {
                        setSelectedGifs(without(selectedGifs, id))
                    } else {
                        setSelectedGifs([...selectedGifs, id])
                    }
                }
                setLastSelected(id)
            }
        },
        [
            // exhaustive deps suck
            allowBulkEdit,
            gifs,
            history,
            iSelectionMode,
            lastSelected,
            openBulkEditPanel,
            selectedGifs,
            sendGAGifClick,
            setSelectedGifs,
            standaloneSelectionMode,
        ]
    )
    const gifWidth = getGifWidthForTV(width, gutter, columns)
    let tvProps: TVProps | undefined
    let offset = 0
    let offsetIndexes: number[] = []
    if (tvConfig) {
        tvProps = getTvProps(tvConfig, gifWidth, gutter, columns)
        offset = tvProps.offset
        offsetIndexes = tvProps.indexes
    }
    const gridOverlayValue = useSameObjectRef({ onGifClick, selectedGifs, iSelectionMode, Overlay })
    return (
        <>
            <Container>
                {tvProps && <TV {...tvProps} />}
                <GridOverlayContext.Provider value={gridOverlayValue}>
                    <GridWithTV
                        user={user}
                        onGifsFetchError={onError}
                        offsetIndexes={offsetIndexes}
                        offset={offset}
                        width={width}
                        gutter={gutter}
                        onGifClick={onGifClick}
                        onGifRightClick={onGifRightClick}
                        columns={columns}
                        fetchGifs={fetchWrapped}
                        overlay={GridOverlay}
                        className={className}
                    />
                </GridOverlayContext.Provider>
            </Container>
            {!standaloneSelectionMode && <BulkEditor />}
        </>
    )
}

export default Grid
